import { useEffect, useMemo, useState } from "react";
import { api, unwrap, API_BASE } from "@/lib/api";
import { ChevronDown, ChevronRight, MessageCircle, Cake, BellRing, Megaphone, Receipt as ReceiptIcon, CalendarDays, Send, Download, FileSpreadsheet, Activity, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  birthday_voucher:  { icon: Cake,         color: "#D4A853", label: "Birthday voucher" },
  waitlist_promote:  { icon: BellRing,     color: "#F59E0B", label: "Waitlist promote" },
  broadcast_delay:   { icon: Megaphone,    color: "#7C5C7A", label: "Broadcast delay" },
  receipt:           { icon: ReceiptIcon,  color: "#15803D", label: "Receipt" },
  booking_confirmed: { icon: CalendarDays, color: "#5E4060", label: "Booking confirmed" },
  schedule_publish:  { icon: CalendarDays, color: "#5E4060", label: "Schedule publish" },
  win_back:          { icon: Send,         color: "#7C3AED", label: "Win-back" },
};

const STATUS_STYLE = {
  mocked: { bg: "rgba(245,158,11,0.10)", color: "#B45309", border: "rgba(245,158,11,0.30)" },
  sent:   { bg: "rgba(22,163,74,0.10)",  color: "#15803D", border: "rgba(22,163,74,0.30)" },
  failed: { bg: "rgba(185,28,28,0.10)",  color: "#991B1B", border: "rgba(185,28,28,0.30)" },
};

const ALL_TYPES = [
  "birthday_voucher", "waitlist_promote", "broadcast_delay",
  "receipt", "booking_confirmed", "schedule_publish", "win_back",
];

function todayStr() { return new Date().toISOString().slice(0, 10); }

function latencyTone(ms) {
  if (ms === null || ms === undefined) return null;
  if (ms < 500) return "#15803D";
  if (ms <= 2000) return "#B45309";
  return "#991B1B";
}

export function NotificationsLog({ branchId, salonName, branchName }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const [filters, setFilters] = useState({
    from_date: todayStr(),
    to_date: todayStr(),
    type: "all",
    status: "all",
  });

  const buildParams = (extra = {}) => {
    const p = { ...extra };
    if (branchId) p.branch_id = branchId;
    if (filters.type && filters.type !== "all") p.type = filters.type;
    if (filters.status && filters.status !== "all") p.status = filters.status;
    if (filters.from_date) p.from = filters.from_date;
    if (filters.to_date)   p.to   = filters.to_date;
    return p;
  };

  useEffect(() => {
    if (!open) return;
    let active = true;
    const fetchAll = async () => {
      try {
        setLoading(true);
        const params = buildParams({ page: 1, page_size: 50 });
        const [list, sum] = await Promise.all([
          api.get("/notifications", { params }),
          api.get("/notifications/summary", { params: { ...(branchId ? { branch_id: branchId } : {}), from: filters.from_date, to: filters.to_date } }),
        ]);
        if (active) {
          setItems(unwrap(list) || []);
          setTotal(list?.data?.meta?.total || 0);
          setSummary(unwrap(sum));
        }
      } catch {} finally { if (active) setLoading(false); }
    };
    fetchAll();
    const i = setInterval(fetchAll, 10000);
    return () => { active = false; clearInterval(i); };
  }, [open, branchId, filters.from_date, filters.to_date, filters.type, filters.status]); // eslint-disable-line

  const download = async (format) => {
    setDownloading(format);
    try {
      const params = buildParams({ format });
      if (salonName)  params.salon_name = salonName;
      if (branchName) params.branch_name = branchName;
      const qs = new URLSearchParams(params).toString();
      const token = localStorage.getItem("gloss.token");
      const res = await fetch(`${API_BASE}/notifications/export?${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gloss-notifications-${todayStr()}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`, { description: "Saved to your downloads folder" });
    } catch (e) {
      toast.error(`Couldn't export ${format.toUpperCase()}`);
    } finally { setDownloading(null); }
  };

  return (
    <div className="glass-primary rounded-[20px] overflow-hidden" data-testid="owner-notifications-log">
      <button onClick={() => setOpen((x) => !x)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/30 transition" data-testid="notif-log-toggle" aria-expanded={open}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(124,92,122,0.12)" }}>
            <MessageCircle size={16} className="text-[var(--color-mauve)]" />
          </div>
          <div className="text-left">
            <div className="font-sora font-semibold text-[14.5px]">Notifications log</div>
            <div className="font-inter text-[12px] text-[var(--color-text-muted)]">Every WhatsApp / SMS / email — log + SLA + export.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 font-inter text-[12.5px] text-[var(--color-text-muted)]">
          {open ? `${total} matching · ${summary?.total || 0} in window` : "Show"}
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </button>

      {open && (
        <div className="px-6 pb-5 anim-slide-up space-y-4">
          {/* SLA summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="notifications-sla-card">
            <SlaTile label="Avg latency" value={`${summary?.avg_latency_ms || 0}ms`} icon={Activity} color="#B45309" />
            <SlaTile label="SLA breaches" value={summary?.sla_breaches ?? 0} icon={AlertTriangle} color="#991B1B" />
            <SlaTile label="Success rate" value={`${summary?.success_rate_pct ?? 0}%`} icon={Send} color="#15803D" />
            <SlaTile label="Total sent" value={`${summary?.sent || 0} · ${summary?.mocked || 0}m`} icon={MessageCircle} color="#5E4060" />
          </div>

          {/* Filters + export */}
          <div className="rounded-2xl p-3 bg-white/50 border border-white/80 flex flex-wrap items-end gap-2">
            <FilterField label="From">
              <input type="date" value={filters.from_date} onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))} className={inputCls} data-testid="notif-filter-from" />
            </FilterField>
            <FilterField label="To">
              <input type="date" value={filters.to_date} onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))} className={inputCls} data-testid="notif-filter-to" />
            </FilterField>
            <FilterField label="Type">
              <select value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} className={inputCls} data-testid="notif-filter-type">
                <option value="all">All types</option>
                {ALL_TYPES.map((t) => <option key={t} value={t}>{TYPE_META[t]?.label || t}</option>)}
              </select>
            </FilterField>
            <FilterField label="Status">
              <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={inputCls} data-testid="notif-filter-status">
                <option value="all">All</option>
                <option value="sent">Sent</option>
                <option value="mocked">Mocked</option>
                <option value="failed">Failed</option>
              </select>
            </FilterField>

            <div className="flex-1" />

            <button onClick={() => download("csv")} className="btn-3d btn-glass-3d btn-sm" disabled={downloading === "csv"} data-testid="notif-export-csv">
              {downloading === "csv" ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />} CSV
            </button>
            <button onClick={() => download("pdf")} className="btn-3d btn-glass-3d btn-sm" disabled={downloading === "pdf"} data-testid="notif-export-pdf">
              {downloading === "pdf" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF
            </button>
          </div>

          {/* Table */}
          {loading && items.length === 0 ? (
            <div className="font-inter text-[13px] text-[var(--color-text-muted)] py-3">Loading…</div>
          ) : items.length === 0 ? (
            <div className="font-inter text-[13px] text-[var(--color-text-muted)] py-3">No notifications match this filter.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/40">
                    <th className="overline text-left px-2 py-2">Type</th>
                    <th className="overline text-left px-2 py-2">Client</th>
                    <th className="overline text-left px-2 py-2">Channel</th>
                    <th className="overline text-left px-2 py-2">Status</th>
                    <th className="overline text-right px-2 py-2">Latency</th>
                    <th className="overline text-right px-2 py-2">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((n) => {
                    const meta = TYPE_META[n.type] || { icon: MessageCircle, color: "#78716C", label: n.type };
                    const Icon = meta.icon;
                    const ss = STATUS_STYLE[n.status] || STATUS_STYLE.mocked;
                    const latColor = latencyTone(n.sla_ms);
                    return (
                      <tr key={n.id} className="border-b border-white/30 hover:bg-white/40 transition">
                        <td className="px-2 py-2.5 font-sora text-[12.5px] font-semibold">
                          <div className="flex items-center gap-2">
                            <Icon size={13} style={{ color: meta.color }} />
                            {meta.label}
                          </div>
                          {n.voucher_code && <div className="font-mono text-[10.5px] text-[var(--color-text-muted)] mt-0.5">{n.voucher_code}{n.amount ? ` · ₹${n.amount}` : ""}</div>}
                        </td>
                        <td className="px-2 py-2.5 font-inter text-[12.5px]">{n.client_name || "—"}</td>
                        <td className="px-2 py-2.5 font-mono text-[11.5px] uppercase text-[var(--color-text-muted)]">{n.channel}</td>
                        <td className="px-2 py-2.5">
                          <span className="status-pill" style={{ background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}>
                            <span className="dot" style={{ background: ss.color }} />
                            {n.status}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-[12px]" style={{ color: latColor || "var(--color-text-muted)" }}>
                          {n.sla_ms != null ? `${n.sla_ms}ms` : "—"}
                          {n.sla_breached && <span className="ml-1 text-[10px]" style={{ color: "#991B1B" }}>!</span>}
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-[11px] text-[var(--color-text-muted)]">
                          {n.created_at ? new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const inputCls = "h-9 px-2.5 rounded-lg bg-white/85 border border-white/85 font-inter text-[12.5px] outline-none focus:border-[var(--color-gold-mid)]";

function FilterField({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="overline text-[10px]">{label}</span>
      {children}
    </label>
  );
}

function SlaTile({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl p-3.5 bg-white/55 border border-white/80">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={13} style={{ color }} />
        <span className="overline" style={{ color }}>{label}</span>
      </div>
      <div className="font-mono leading-none" style={{ fontSize: 22, color }}>{value}</div>
    </div>
  );
}

export default NotificationsLog;
